import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import styles from './SeriesThreePlusSelection.module.scss';
import SeriesThreePlusModule from '../../assets/images/series_three_plus_module.png';
import BehaviorRunningVideo from '../../assets/images/behavior-running-mobile.mp4';
import BehaviorRunningPoster from '../../assets/images/behavior-running-mobile-first.webp';
import BehaviorEatingVideo from '../../assets/images/behavior-eating-mobile.mp4';
import BehaviorEatingPoster from '../../assets/images/behavior-eating-mobile-first.webp';
import BehaviorLickingVideo from '../../assets/images/behavior-licking-mobile.mp4';
import BehaviorLickingPoster from '../../assets/images/behavior-licking-mobile-first.webp';
import BehaviorBarkingVideo from '../../assets/images/behavior-barking-mobile.mp4';
import BehaviorBarkingPoster from '../../assets/images/behavior-barking-mobile-first.webp';
import SeriesThreePlusTech1 from '../../assets/images/series_three_plus_tech_1.webp';
import SeriesThreePlusTech2 from '../../assets/images/series_three_plus_tech_2.webp';
import SeriesThreePlusTech3 from '../../assets/images/series_three_plus_tech_3.webp';
import SeriesThreePlusTech4 from '../../assets/images/series_three_plus_tech_4.webp';
import SeriesThreePlusTech5 from '../../assets/images/series_three_plus_tech_5.webp';
import StatActivity from '../../assets/images/stat_activity.png';
import StatRest from '../../assets/images/stat_rest.png';
import StatEating from '../../assets/images/stat_eating.png';
import StatDrinking from '../../assets/images/stat_drinking.png';
import StatLicking from '../../assets/images/stat_licking.png';
import StatScratching from '../../assets/images/stat_scratching.png';
import StatBarking from '../../assets/images/stat_barking.png';
import SeriesThreePlusLogo from '../../assets/images/logo_series_three_plus.svg';
import Button from '../../components/Button';
import { Carousel, CarouselController } from '../../components/Carousel';
import { ReactComponent as IconBell } from '../../assets/images/icons/icon_bell.svg';
import { ReactComponent as IconVet } from '../../assets/images/icons/icon_vet_black.svg';
import { ReactComponent as IconLight } from '../../assets/images/icons/icon_light.svg';
import { ReactComponent as IconBattery } from '../../assets/images/icons/icon_battery_charging.svg';
import { ReactComponent as IconReinforced } from '../../assets/images/icons/icon_reinforced.svg';
import { ReactComponent as IconDrip } from '../../assets/images/icons/icon_drip.svg';
import { ReactComponent as IconTracking } from '../../assets/images/icons/icon_tracking.svg';
import { ReactComponent as CloseIcon } from '../../assets/images/icons/close.svg';
import SeriesThreePlusHeroPoster from '../../assets/images/series_three_plus_poster.webp';
import SeriesThreePlusHeroVideo from '../../assets/images/series_three_plus_hero.mp4';
import SeriesThreePlusConfirmationModal from './SeriesThreePlusConfirmationModal';
import {
  previewUpgradeToSeriesThreePlusModuleQuery,
  purchaseUpgradeToSeriesThreePlusModuleMutation,
} from '../../graphql-operations/UpgradeToSeriesThreePlus.graphql';
import { Address, BillingInfo, gqlTypes, isModuleSubscriptionProduct } from '../../types';
import { useMutation, useQuery } from '@apollo/client';
import { usePurchaseSubscriptionContext } from '../PurchaseSubscription/context/PurchaseSubscriptionContext';
import { DateTime } from 'luxon';
import { useError } from '../../components/ErrorMessage';
import { centsToDollars, formattedCentsToDollars, getLoadingOrErrorElement } from '../../lib/util';
import { billingInfoQuery, series3UpgradeOfferQuery } from '../../graphql-operations';
import { useHistory } from 'react-router-dom';
import AppPaths from '../../AppPaths';
import { seriesThreePlusUpgrade } from '../../lib/analytics/events';
import { useDispatch, useSelector } from 'react-redux';
import * as types from '../../types';
import { series3UpgradeShopActions } from '../../reducers/series3UpgradeShop';
import { cartActions as upgradeCartActions } from '../../reducers/series3UpgradeCart';
import useProductSkuMap from '../../hooks/useProductSkuMap';
import { getBillingCadenceDescription } from '../ManageSubscription/util/helpers';
import { useLocale } from '../../contexts/LocaleContext';
import { getFiGrowthBook, useIsGrowthBookEnabled } from '../../lib/growthbook';
import { SERIES_3_UPGRADE_V2_COUPON_CODE } from '../Series3UpgradeV2/Series3UpgradePDPV2';

export type PreviewPrice = {
  subtotalInCents: number;
  taxInCents: number;
  totalInCents: number;
  shippingInCents: number;
};

export default function SeriesThreePlusSelection() {
  const { t } = useTranslation();
  const growthBookEnabled = useIsGrowthBookEnabled();
  const isBfcmUpgradeEnabled =
    growthBookEnabled && getFiGrowthBook().getFeatureValue<boolean>('ecom-bfcm-upgrade', false);
  const bfcmBadge = growthBookEnabled ? getFiGrowthBook().getFeatureValue<number>('ecom-bfcm-badge', 0) : 0;

  useEffect(() => seriesThreePlusUpgrade.viewed(), []);

  const [behaviorCarouselIndex, setBehaviorCarouselIndex] = React.useState(0);
  const [techCarouselIndex, setTechCarouselIndex] = React.useState(0);
  const [behaviorUserInteracted, setBehaviorUserInteracted] = React.useState(false);
  const [techUserInteracted, setTechUserInteracted] = React.useState(false);
  const behaviorCarouselControllerRef = React.useRef<CarouselController>();
  const techCarouselControllerRef = React.useRef<CarouselController>();
  const { error, setError } = useError();

  const behaviorObserver = React.useRef<IntersectionObserver | null>(null);
  const techObserver = React.useRef<IntersectionObserver | null>(null);
  const behaviorInterval = React.useRef<number | null>(null);
  const techInterval = React.useRef<number | null>(null);
  const isAutoAdvancingBehavior = React.useRef<boolean>(false);
  const isAutoAdvancingTech = React.useRef<boolean>(false);

  const { showFallbackHeroImage, heroBackgroundVideo } = useShowHeroBackgroundVideo();

  const {
    deviceId,
    currentTermEndsAt,
    billingInfo,
    shippingAddress,
    refetchBillingInfo,
    currentSubscriptionPriceInCents,
    isAlreadyUpgraded,
    petId,
    isLegacyDevice,
  } = useUpgradeDetails();

  const { previewPrice, previewLoading } = usePreviewUpgradeToSeriesThreePlusModuleQuery(deviceId, setError);

  const { purchaseUpgradeToSeriesThreePlusModule, purchaseLoading } = usePurchaseUpgradeToSeriesThreePlusModuleMutation(
    deviceId,
    setError,
  );

  useRedirectToSuccessPageIfAlreadyUpgraded(isAlreadyUpgraded, deviceId);

  const onContinueUpgradeLegacy = useLegacyCollarToSeriesThreePlus(deviceId, isBfcmUpgradeEnabled);

  const { prorateCreditInCents, adjustedPriceInCents, prorateLoading, formattedRenewalPrice, renewalMonths } =
    useProrateDiscountLegacySubscriptionOffer(true, petId, setError);

  let displayPrice: string;
  let strikethroughPriceValue: string | null = null;
  if (isBfcmUpgradeEnabled) {
    if (isLegacyDevice) {
      displayPrice = '$99';
      strikethroughPriceValue = '$189';
    } else {
      displayPrice = '$79';
      strikethroughPriceValue = '$99';
    }
  } else {
    displayPrice = isLegacyDevice ? formattedCentsToDollars(adjustedPriceInCents) : '$99';
  }

  const previewUpgradeLoadingOrErrorElement = getLoadingOrErrorElement(
    previewLoading || purchaseLoading || prorateLoading,
    error
      ? new Error(error)
      : currentSubscriptionPriceInCents === undefined
        ? new Error(t('unknown_subscription_plan'))
        : undefined,
  );

  const startBehaviorAutoAdvance = React.useCallback(() => {
    if (behaviorUserInteracted) return;
    if (behaviorInterval.current) window.clearInterval(behaviorInterval.current);
    behaviorInterval.current = window.setInterval(() => {
      setBehaviorCarouselIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % 4; // 4 behavior slides
        isAutoAdvancingBehavior.current = true;
        behaviorCarouselControllerRef.current?.select(nextIndex, true);
        return nextIndex;
      });
    }, 5000);
  }, [behaviorUserInteracted]);

  const stopBehaviorAutoAdvance = React.useCallback(() => {
    if (behaviorInterval.current) {
      window.clearInterval(behaviorInterval.current);
      behaviorInterval.current = null;
    }
  }, []);

  const startTechAutoAdvance = React.useCallback(() => {
    if (techUserInteracted) return;
    if (techInterval.current) window.clearInterval(techInterval.current);
    techInterval.current = window.setInterval(() => {
      setTechCarouselIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % 5; // 5 tech slides
        isAutoAdvancingTech.current = true;
        techCarouselControllerRef.current?.select(nextIndex, true);
        return nextIndex;
      });
    }, 5000);
  }, [techUserInteracted]);

  const stopTechAutoAdvance = React.useCallback(() => {
    if (techInterval.current) {
      window.clearInterval(techInterval.current);
      techInterval.current = null;
    }
  }, []);

  // Callback ref for behavior carousel
  const setBehaviorCarouselRef = React.useCallback(
    (element: HTMLDivElement | null) => {
      if (element) {
        behaviorObserver.current = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                startBehaviorAutoAdvance();
              } else {
                stopBehaviorAutoAdvance();
              }
            });
          },
          { threshold: 0.5 },
        );
        behaviorObserver.current.observe(element);
      } else if (behaviorObserver.current) {
        behaviorObserver.current.disconnect();
        behaviorObserver.current = null;
      }
    },
    [startBehaviorAutoAdvance, stopBehaviorAutoAdvance],
  );

  // Callback ref for tech carousel
  const setTechCarouselRef = React.useCallback(
    (element: HTMLDivElement | null) => {
      if (element) {
        techObserver.current = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                startTechAutoAdvance();
              } else {
                stopTechAutoAdvance();
              }
            });
          },
          { threshold: 0.5 },
        );
        techObserver.current.observe(element);
      } else if (techObserver.current) {
        techObserver.current.disconnect();
        techObserver.current = null;
      }
    },
    [startTechAutoAdvance, stopTechAutoAdvance],
  );

  useEffect(() => {
    return () => {
      stopBehaviorAutoAdvance();
      stopTechAutoAdvance();
      if (behaviorObserver.current) behaviorObserver.current.disconnect();
      if (techObserver.current) techObserver.current.disconnect();
    };
  }, [stopBehaviorAutoAdvance, stopTechAutoAdvance]);

  if (previewUpgradeLoadingOrErrorElement) {
    return previewUpgradeLoadingOrErrorElement;
  }

  const triggerSeriesThreePlusModal = (
    <div className={styles.addToCartContainer}>
      <Button
        className={styles.simpleButton}
        disabled={false}
        onClick={() => seriesThreePlusUpgrade.confirmationModalOpened()}
      >
        {t('get_it_now')}
      </Button>
      <p className={styles.savingsText}>
        <Trans
          i18nKey="no_changes_to_your_current_membership_your_plan_renews_on_date"
          values={{ date: currentTermEndsAt }}
          components={{ br: <br /> }}
        />
      </p>
    </div>
  );

  const seriesThreePlusLegacyUpgrade = (
    <div className={styles.addToCartContainer}>
      <Button className={styles.simpleButton} disabled={false} onClick={() => onContinueUpgradeLegacy()}>
        {t('get_it_now')}
      </Button>
      <p className={styles.savingsText}>
        <Trans
          i18nKey="well_cover_your_subscription_until_your_s3_arrives_plan_renews_at_price"
          values={{ price: formattedRenewalPrice }}
          components={{ br: <br /> }}
        />
      </p>
    </div>
  );

  return (
    <div className={styles.simpleFullWidthWrapper}>
      <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />

      <div
        className={styles.closeButton}
        onClick={() => {
          window.open('fi://closeappview');
        }}
      >
        <CloseIcon />
      </div>
      <div className={styles.heroBackground}>
        {showFallbackHeroImage ? (
          <img className={styles.videoBackground} src={SeriesThreePlusHeroPoster} alt={t('hero')} />
        ) : (
          <video
            ref={heroBackgroundVideo}
            className={styles.videoBackground}
            src={SeriesThreePlusHeroVideo}
            poster={SeriesThreePlusHeroPoster}
            autoPlay
            muted
            loop
            playsInline
          />
        )}
      </div>

      <div className={styles.simpleContainer}>
        <div className={styles.newChip}>
          <p>{t('new')}</p>
        </div>

        <div className={styles.simpleHeaderContainer}>
          <img className={styles.simpleHeader} src={SeriesThreePlusLogo} alt={t('series_three_plus_module')} />
          <p className={styles.simpleSubheader}>
            <Trans i18nKey="meet_the_worlds_smartest_ai_powered_gps_collar" components={{ br: <br /> }} />
          </p>
        </div>

        <div className={styles.offerContainer}>
          <div className={styles.offerDetailsContainer}>
            <div className={styles.upsellSubscriptionRow} style={{ display: 'flex', flexDirection: 'row' }}>
              <img className={styles.module} src={SeriesThreePlusModule} alt={t('series_three_plus_module')} />
              <div className={styles.upsellSubscriptionColumn} style={{ display: 'flex', flexDirection: 'column' }}>
                <p className={styles.upsellSubscriptionTermText}>
                  {isBfcmUpgradeEnabled
                    ? bfcmBadge === 1
                      ? t('black_fi_day_offer_caps')
                      : t('cyber_monday_offer_caps')
                    : isLegacyDevice
                      ? renewalMonths === 24
                        ? t('your_first_term_offer')
                        : t('your_first_year_offer')
                      : t('special_upgrade_offer')}
                </p>

                <p className={styles.upsellSubscriptionPriceText}>
                  {displayPrice}
                  {strikethroughPriceValue && (
                    <>
                      &nbsp;
                      <span className={styles.strikethroughPrice}>{strikethroughPriceValue}</span>
                    </>
                  )}
                </p>
              </div>
            </div>
            <ul className={styles.benefitsList}>
              <li className={styles.benefitItem}>
                {isLegacyDevice
                  ? isBfcmUpgradeEnabled
                    ? t('new_series_3_plus_collar')
                    : t('get_our_new_collar_to_unlock_ai_powered_insights')
                  : t('one_time_payment')}
              </li>
              <li className={styles.benefitItem}>
                {isLegacyDevice ? (
                  isBfcmUpgradeEnabled ? (
                    t('one_year_fi_membership')
                  ) : (
                    <Trans
                      i18nKey="credit_from_current_plan_applied"
                      values={{ creditAmount: centsToDollars(prorateCreditInCents) }}
                      components={{ span: <span className={styles.prorate} /> }}
                    />
                  )
                ) : (
                  t('keep_your_membership_just_pay_for_the_device')
                )}
              </li>
              <li className={styles.benefitItem}>
                {isLegacyDevice
                  ? t('your_plan_will_renew_at_price_moving_forward', { price: formattedRenewalPrice })
                  : t('swap_in_the_new_device_to_your_existing_collar')}
              </li>
            </ul>
          </div>
        </div>

        <div className={styles.whatsNewContainer}>
          <p className={styles.whatsNewHeader}>
            <Trans i18nKey="dig_into_whats_new" components={{ br: <br /> }} />
          </p>
          <p className={styles.whatsNewSubheader}>
            <Trans i18nKey="breakthrough_behavior_translator" components={{ br: <br /> }} />
          </p>
        </div>

        <CarouselDots
          activeIndex={behaviorCarouselIndex}
          total={4}
          onClick={(index) => {
            setBehaviorUserInteracted(true);
            stopBehaviorAutoAdvance();
            setBehaviorCarouselIndex(index);
            behaviorCarouselControllerRef.current?.select(index, true);
          }}
        />

        <div ref={setBehaviorCarouselRef}>
          <Carousel
            className={styles.mobileAppCarousel}
            cellAlign="center"
            wrapAround={false}
            prevNextButtons={false}
            controllerRef={behaviorCarouselControllerRef}
            onChange={(index) => {
              setBehaviorCarouselIndex(index);
              if (!isAutoAdvancingBehavior.current) {
                setBehaviorUserInteracted(true);
                stopBehaviorAutoAdvance();
              }
              isAutoAdvancingBehavior.current = false;
            }}
          >
            <FeatureImage
              videoSrc={BehaviorRunningVideo}
              poster={BehaviorRunningPoster}
              header={t('activity_plus_rest')}
              subHeader={t('hit_breed_based_fitness_goals_and_catch_changes')}
              behaviorImageSrc={[StatActivity, StatRest]}
            />
            <FeatureImage
              videoSrc={BehaviorEatingVideo}
              poster={BehaviorEatingPoster}
              header={t('eating_plus_drinking')}
              subHeader={t('spot_meaningful_shifts_in_appetite_and_thirst')}
              behaviorImageSrc={[StatEating, StatDrinking]}
            />
            <FeatureImage
              videoSrc={BehaviorLickingVideo}
              poster={BehaviorLickingPoster}
              header={t('licking_plus_scratching')}
              subHeader={t('keep_tabs_on_your_dogs_skin_health')}
              behaviorImageSrc={[StatLicking, StatScratching]}
            />
            <FeatureImage
              videoSrc={BehaviorBarkingVideo}
              poster={BehaviorBarkingPoster}
              header={t('barking')}
              subHeader={t('get_notified_of_unusually_vocal_or_quiet_days')}
              behaviorImageSrc={[StatBarking]}
            />
          </Carousel>
        </div>

        <div className={`${styles.whatsNewContainer} ${styles.whatsNewContainerExtraTopMargin}`}>
          <p className={styles.whatsNewSubheader}>{t('packed_full_of_tech')}</p>
        </div>
        <CarouselDots
          activeIndex={techCarouselIndex}
          total={5}
          onClick={(index) => {
            setTechUserInteracted(true);
            stopTechAutoAdvance();
            setTechCarouselIndex(index);
            techCarouselControllerRef.current?.select(index, true);
          }}
        />

        <div ref={setTechCarouselRef}>
          <Carousel
            className={styles.mobileAppCarousel}
            cellAlign="center"
            wrapAround={false}
            prevNextButtons={false}
            controllerRef={techCarouselControllerRef}
            onChange={(index) => {
              setTechCarouselIndex(index);
              if (!isAutoAdvancingTech.current) {
                setTechUserInteracted(true);
                stopTechAutoAdvance();
              }
              isAutoAdvancingTech.current = false;
            }}
          >
            <FeatureImage
              src={SeriesThreePlusTech1}
              header={t('2x_improved_gps')}
              subHeader={t('series_3_plus_outperforms_previous_generations')}
            />
            <FeatureImage
              src={SeriesThreePlusTech2}
              header={t('ai_companion')}
              subHeader={t('meet_your_go_to_resource_for_all_things_dog_parenting')}
            />
            <FeatureImage
              src={SeriesThreePlusTech3}
              header={t('ai_vet_records')}
              subHeader={t('automatically_organize_and_summarize_all_your_dogs_key_paperwork')}
            />
            <FeatureImage
              src={SeriesThreePlusTech4}
              header={t('tailored_dashboard')}
              subHeader={t('know_what_matters_most_with_an_all_in_one_app')}
            />
            <FeatureImage
              src={SeriesThreePlusTech5}
              header={t('apple_watch_integration')}
              subHeader={t('real_time_health_and_safety_insights_are_never_more_than_a_glance_away')}
            />
          </Carousel>
        </div>
        <div className={`${styles.whatsNewContainer} ${styles.whatsNewContainerExtraTopMargin}`}>
          <p className={styles.whatsNewSubheader}>{t('plus_all_the_features_dog_parents_love')}</p>
        </div>
        <FeatureList />
      </div>

      {/* Safely display modal with valid pricing data. If price data is not valid then we return an error element above. */}
      {isLegacyDevice && seriesThreePlusLegacyUpgrade}
      {previewPrice && !isLegacyDevice && (
        <SeriesThreePlusConfirmationModal
          trigger={triggerSeriesThreePlusModal}
          billingInfo={billingInfo ? (billingInfo as BillingInfo) : undefined}
          shippingAddress={shippingAddress as Address}
          previewPrice={previewPrice}
          renewalPriceInCents={currentSubscriptionPriceInCents!}
          renewalDate={currentTermEndsAt}
          onAccept={() => {
            seriesThreePlusUpgrade.confirmationModalContinue();
            purchaseUpgradeToSeriesThreePlusModule();
          }}
          onUpdateBilling={() => {
            refetchBillingInfo();
          }}
        />
      )}
    </div>
  );
}

const FeatureList = () => {
  const { t } = useTranslation();
  const features = [
    {
      icon: <IconTracking />,
      title: t('lost_dog_mode'),
      description: t('use_both_gps_lte_m_to_get_real_time_location_updates'),
    },
    {
      icon: <IconBell />,
      title: t('escape_alerts_virtual_fences'),
      description: t('easily_set_safe_zones_and_get_instant_alerts_if_they_stray'),
    },
    {
      icon: <IconVet />,
      title: t('ai_vet_records_feature'),
      description: t('upload_your_dogs_documents_and_have_them_automatically_organized'),
    },
    {
      icon: <IconDrip />,
      title: t('fully_waterproof'),
      description: t('ip68_66k_tested_in_salt_water_up_to_50_meters_no_period'),
    },
    {
      icon: <IconLight />,
      title: t('color_night_light'),
      description: t('keep_your_dog_visible_even_in_the_dark_no_period'),
    },
    {
      icon: <IconReinforced />,
      title: t('500lbs_plus_resistance'),
      description: t('handles_the_worst_chewers_and_pullers'),
    },
    {
      icon: <IconBattery />,
      title: t('long_battery_life'),
      description: t('up_to_3_months_on_one_charge_no_period'),
    },
  ];

  return (
    <div className={styles.featureList}>
      {features.map((item, index) => (
        <div key={index} className={styles.featureItem}>
          <div className={styles.icon}>{item.icon}</div>
          <div className={styles.text}>
            <div className={styles.title}>{item.title}</div>
            <div className={styles.description}>{item.description}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

const CarouselDots: React.FC<{ total: number; activeIndex: number; onClick: (index: number) => void }> = ({
  total,
  activeIndex,
  onClick,
}) => (
  <div className={styles.carouselIndicator}>
    {Array.from({ length: total }).map((_, i) => (
      <div
        key={i}
        className={`${styles.dot} ${i === activeIndex ? styles.active : styles.inactive}`}
        onClick={() => onClick(i)}
        style={{ cursor: 'pointer' }}
      />
    ))}
  </div>
);

interface FeatureImageProps {
  src?: string; // for images
  videoSrc?: string; // for videos
  poster?: string; // poster for videos
  header?: string;
  subHeader?: string;
  behaviorImageSrc?: string[];
}

const FeatureImage: React.FC<FeatureImageProps> = ({ src, videoSrc, poster, header, subHeader, behaviorImageSrc }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!videoSrc || !videoRef.current || !containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            videoRef.current?.play();
          } else {
            videoRef.current?.pause();
          }
        });
      },
      { threshold: 0.5 },
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [videoSrc]);

  return (
    <div ref={containerRef} className={styles.imageContainer}>
      {videoSrc ? (
        <video ref={videoRef} className={styles.mobileAppImage} src={videoSrc} poster={poster} muted loop playsInline />
      ) : (
        <img className={styles.mobileAppImage} src={src} alt="" />
      )}
      <div className={styles.imageOverlay}>
        <div className={styles.imageOverlayTopText}>
          <div className={styles.overlayHeader}>{header}</div>
          <div className={styles.overlaySubheader}>{subHeader}</div>
        </div>
        <div className={styles.imageOverlayBottomBehavior}>
          {behaviorImageSrc && behaviorImageSrc.length > 0 && (
            <>
              {behaviorImageSrc.map((behaviorSrc, index) => (
                <img
                  key={index}
                  className={styles.behaviorImage}
                  src={behaviorSrc}
                  alt={'stats_' + index}
                  loading="lazy"
                />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const useUpgradeDetails = () => {
  const { device } = usePurchaseSubscriptionContext();
  const deviceId = device.id;
  const petId = device.pet!.id;

  const subscription = device.subscription!; // Dangerous! However, it should be there.
  const currentSubscriptionPriceInCents = subscription.subscriptionOption?.priceInCents;

  const currentTermEndsAt = DateTime.fromISO(subscription.currentTermEndsAt).toFormat('M/d/yyyy');
  const { billingInfo, shippingAddress, refetchBillingInfo } = useBillingInfoQuery();

  const isAlreadyUpgraded = !!device.subscription?.subscriptionOption?.sku?.includes('f3b');

  const isLegacyDevice = device.currentCollarModel === 'S1' || device.currentCollarModel === 'S2';

  return {
    deviceId,
    currentTermEndsAt,
    billingInfo,
    shippingAddress,
    refetchBillingInfo,
    currentSubscriptionPriceInCents,
    isAlreadyUpgraded,
    petId: petId,
    isLegacyDevice,
  };
};

const useRedirectToSuccessPageIfAlreadyUpgraded = (isAlreadyUpgraded: boolean, deviceId: string) => {
  const history = useHistory();

  useEffect(() => {
    if (isAlreadyUpgraded) {
      history.push(AppPaths.Series3PlusUpgrade.Complete(deviceId));
    }
  }, [isAlreadyUpgraded, history, deviceId]);
};

const useBillingInfoQuery = () => {
  const { data, refetch } = useQuery<gqlTypes.ECOMMERCE_billingInfoFromBillingAccount>(billingInfoQuery);

  const billingInfo = data?.currentUser.billingAccount?.billingInfo;
  const shippingAddress = data?.currentUser.billingAccount?.address;

  return { billingInfo, shippingAddress, refetchBillingInfo: refetch };
};

function usePreviewUpgradeToSeriesThreePlusModuleQuery(
  moduleId: string,
  setError: (error: string) => void,
): { previewPrice?: PreviewPrice; previewLoading: boolean } {
  const previewResult = useQuery<
    gqlTypes.ECOMMERCE_previewUpgradeToSeriesThreePlusModule,
    gqlTypes.QuerypreviewUpgradeToSeriesThreePlusModuleArgs
  >(previewUpgradeToSeriesThreePlusModuleQuery, {
    variables: {
      input: {
        moduleId: moduleId,
      },
    },
    onError(err) {
      setError(err.message);
    },
  });

  return {
    previewPrice: previewResult.data?.previewUpgradeToSeriesThreePlusModule,
    previewLoading: previewResult.loading,
  };
}

function usePurchaseUpgradeToSeriesThreePlusModuleMutation(moduleId: string, setError: (error: string) => void) {
  const history = useHistory();

  const [purchaseUpgradeToSeriesThreePlusModule, { loading }] = useMutation<
    gqlTypes.PreviewUpgradeToSeriesThreePlusModuleResult,
    gqlTypes.MutationpurchaseUpgradeToSeriesThreePlusModuleArgs
  >(purchaseUpgradeToSeriesThreePlusModuleMutation, {
    variables: {
      input: {
        moduleId: moduleId,
      },
    },
    onCompleted() {
      history.push({ pathname: AppPaths.Series3PlusUpgrade.Complete(moduleId) });
    },
    onError(err) {
      setError(err.message);
    },
  });

  return { purchaseUpgradeToSeriesThreePlusModule, purchaseLoading: loading };
}

function useProrateDiscountLegacySubscriptionOffer(
  isLegacyDevice: boolean,
  petId: string,
  setError: (error: string) => void,
) {
  const { data, loading: offerLoading } = useQuery<
    gqlTypes.ECOMMERCE_series3UpgradeOffer,
    gqlTypes.ECOMMERCE_series3UpgradeOfferVariables
  >(series3UpgradeOfferQuery, {
    variables: { petId },
    skip: !isLegacyDevice,
    onError: (err) => {
      if (isLegacyDevice) {
        setError(err.message);
      }
    },
  });
  const { currencyFormatter } = useLocale();
  const productSkuMap = useProductSkuMap();
  const offerSubscriptionSku = data?.series3UpgradeOffer?.planCode;

  const offerProduct = offerSubscriptionSku ? productSkuMap.get(offerSubscriptionSku)?.product : null;
  const isLongTermOffer = offerProduct && isModuleSubscriptionProduct(offerProduct) && offerProduct.renewalMonths > 12;

  // Default to 1 year pricing if the offered plan is less than 2 years
  const renewalMonths = offerProduct && isLongTermOffer ? offerProduct.renewalMonths : 12;
  const productPriceInCents = offerProduct && isLongTermOffer ? offerProduct.priceInCents : 18900;
  const formattedRenewalPrice = getBillingCadenceDescription(productPriceInCents, renewalMonths, currencyFormatter);

  const prorateCreditInCents = data?.series3UpgradeOffer?.deviceOffers[0]?.proratedCreditAmountInCents ?? 0;
  const adjustedPriceInCents = productPriceInCents - prorateCreditInCents;

  return {
    prorateCreditInCents,
    adjustedPriceInCents,
    prorateLoading: isLegacyDevice ? offerLoading : false,
    formattedRenewalPrice,
    renewalMonths,
  };
}

const useLegacyCollarToSeriesThreePlus = (moduleId: string, isBfcmUpgradeEnabled: boolean) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const series3UpgradeCart = useSelector((state: types.AppState) => state.series3UpgradeShop.cart);

  useEffect(() => {
    if (Object.keys(series3UpgradeCart.cartItems).length > 0) {
      dispatch(series3UpgradeShopActions.resetCheckout());
      dispatch(upgradeCartActions.resetCart());
    }
  });

  const onContinueUpgradeLegacy = useCallback(() => {
    if (isBfcmUpgradeEnabled) {
      dispatch(upgradeCartActions.addCoupon(SERIES_3_UPGRADE_V2_COUPON_CODE));
    }
    history.push(AppPaths.Series3Upgrade.ProductSelector(moduleId));
  }, [moduleId, history, isBfcmUpgradeEnabled, dispatch]);

  return onContinueUpgradeLegacy;
};

const useShowHeroBackgroundVideo = () => {
  const [showFallbackHeroImage, setShowFallbackHeroImage] = React.useState(false);
  const heroBackgroundVideo = React.useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const video = heroBackgroundVideo.current;
    if (!video) return;

    // Attempt to play the video, returns true if able to play or already playing.
    // Can throw error by system-level low battery auto play policy.
    video.play().catch(() => {
      setShowFallbackHeroImage(true);
    });
  }, []);

  return { showFallbackHeroImage, heroBackgroundVideo };
};
